import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import TrackingMap from './components/TrackingMap';
import ETACountdown from './components/ETACountdown';
import OrderStatusTimeline from './components/OrderStatusTimeline';
import DeliveryStopList from './components/DeliveryStopList';
import DriverCard from './components/DriverCard';
import OrderActions from './components/OrderActions';
import RatingModal from './components/RatingModal';
import { orderService, realtimeService, driverService } from '../../services/supabaseService';
import { supabase } from '../../lib/supabase';
import SpinnerOverlay from '../../components/ui/SpinnerOverlay';
import SuccessToast from '../../components/ui/SuccessToast';
import SkeletonCard, { SkeletonRow } from '../../components/ui/SkeletonCard';

// ─── Utility functions ────────────────────────────────────────────────────────
const haversineKm = (lat1, lng1, lat2, lng2) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const calculateBearing = (lat1, lng1, lat2, lng2) => {
  const toRad = (d) => (d * Math.PI) / 180;
  const dLng = toRad(lng2 - lng1);
  const y = Math.sin(dLng) * Math.cos(toRad(lat2));
  const x =
    Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
    Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLng);
  const bearing = (Math.atan2(y, x) * 180) / Math.PI;
  return (bearing + 360) % 360;
};

const estimateETA = (distanceKm, speedKmh = 25) =>
  Math.max(1, Math.round((distanceKm / speedKmh) * 60));

const getTrafficMultiplier = () => {
  const hour = new Date()?.getHours();
  if (hour >= 7 && hour < 9) return { multiplier: 1.4, label: 'Heavy', color: '#ef4444' };
  if (hour >= 17 && hour < 20) return { multiplier: 1.6, label: 'Heavy', color: '#ef4444' };
  if ((hour >= 9 && hour < 12) || (hour >= 14 && hour < 17)) return { multiplier: 1.2, label: 'Moderate', color: '#f59e0b' };
  return { multiplier: 1.0, label: 'Light', color: '#22c55e' };
};

const GEOFENCE_RADIUS_M = 200;
const isWithinGeofence = (driverLat, driverLng, destLat, destLng) => {
  if (!driverLat || !driverLng || !destLat || !destLng) return false;
  const distKm = haversineKm(driverLat, driverLng, destLat, destLng);
  return distKm * 1000 <= GEOFENCE_RADIUS_M;
};

const fireBrowserNotification = (title, body) => {
  if (!('Notification' in window)) return;
  const send = () => new Notification(title, { body, icon: '/favicon.ico' });
  if (Notification.permission === 'granted') {
    send();
  } else if (Notification.permission !== 'denied') {
    Notification.requestPermission()?.then(perm => { if (perm === 'granted') send(); });
  }
};

const fetchOrsRoute = async (fromLat, fromLng, toLat, toLng) => {
  try {
    const apiKey = import.meta.env?.VITE_ORS_API_KEY || '5b3ce3597851110001cf6248a7a9c3e7b7e04b3e8a3e3e3e3e3e3e3e';
    const url = `https://api.openrouteservice.org/v2/directions/driving-car?api_key=${apiKey}&start=${fromLng},${fromLat}&end=${toLng},${toLat}`;
    const res = await fetch(url);
    if (!res?.ok) throw new Error('ORS API error');
    const data = await res?.json();
    const coords = data?.features?.[0]?.geometry?.coordinates || [];
    const durationSec = data?.features?.[0]?.properties?.segments?.[0]?.duration || 0;
    return {
      polyline: coords?.map(([lng, lat]) => ({ lat, lng })),
      durationMin: Math.round(durationSec / 60),
    };
  } catch {
    return null;
  }
};

const OrderTrackingScreen = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams?.get('orderId');

  const [order, setOrder] = useState(null);
  const [stops, setStops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sheetExpanded, setSheetExpanded] = useState(false);
  const [routeProgress, setRouteProgress] = useState(35);
  const [showCallModal, setShowCallModal] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [orderCancelled, setOrderCancelled] = useState(false);
  const [currentETA, setCurrentETA] = useState(18);
  const [driverPosition, setDriverPosition] = useState(null);
  const [liveStatus, setLiveStatus] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [isRealGpsStreaming, setIsRealGpsStreaming] = useState(false);
  const [showArrivalModal, setShowArrivalModal] = useState(false);
  const [orderCompleted, setOrderCompleted] = useState(false);
  const [trafficInfo, setTrafficInfo] = useState(getTrafficMultiplier());
  const [routePolyline, setRoutePolyline] = useState([]);
  const [trafficETA, setTrafficETA] = useState(null);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [fullScreenMap, setFullScreenMap] = useState(false);
  const [etaOverlayVisible, setEtaOverlayVisible] = useState(true);
  const [cancellingOrder, setCancellingOrder] = useState(false);
  const [arrivalToast, setArrivalToast] = useState(false);

  const orderChannelRef = useRef(null);
  const locationChannelRef = useRef(null);
  const simulationRef = useRef(null);
  const prevPositionRef = useRef(null);
  const gpsTimeoutRef = useRef(null);
  const geofenceTriggeredRef = useRef(false);
  const routeRefreshRef = useRef(null);

  const mockDriver = {
    name: order?.driver?.name || 'James Mwangi',
    avatar: '👨🏾‍✈️',
    rating: order?.driver?.rating || 4.8,
    trips: order?.driver?.totalTrips || 1247,
    vehicleType: order?.driver?.vehicleType || 'Boda Boda',
    vehicleName: order?.driver?.vehicleName || 'Honda CB125',
    vehicleColor: order?.driver?.vehicleColor || 'Red & Black',
    vehicleYear: '2022',
    licensePlate: order?.driver?.licensePlate || 'KDG 421X',
    phone: order?.driver?.phone || '+254 712 345 678',
  };

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        setLoading(true);
        if (orderId) {
          const orderData = await orderService?.getOrder(orderId);
          setOrder(orderData);
          setStops(orderData?.stops || []);
          setLiveStatus(orderData?.status);
        } else {
          const { data: { user } } = await supabase?.auth?.getUser();
          if (user) {
            const orders = await orderService?.getCustomerOrders(user?.id);
            const activeOrder = orders?.find(o => !['delivered', 'cancelled']?.includes(o?.status));
            if (activeOrder) {
              const fullOrder = await orderService?.getOrder(activeOrder?.id);
              setOrder(fullOrder);
              setStops(fullOrder?.stops || []);
              setLiveStatus(fullOrder?.status);
            }
          }
        }
      } catch (err) {
        console.error('Failed to load order:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [orderId]);

  const recalculateETA = useCallback((newLat, newLng, speedMs) => {
    const dropoffLat = order?.dropoffLat;
    const dropoffLng = order?.dropoffLng;
    if (dropoffLat && dropoffLng && newLat && newLng) {
      const distKm = haversineKm(newLat, newLng, dropoffLat, dropoffLng);
      const speedKmh = speedMs != null && speedMs > 0 ? speedMs * 3.6 : 25;
      const traffic = getTrafficMultiplier();
      setTrafficInfo(traffic);
      const baseEta = estimateETA(distKm, speedKmh);
      const trafficAdjustedEta = Math.round(baseEta * traffic?.multiplier);
      setCurrentETA(trafficAdjustedEta);
      setTrafficETA(trafficAdjustedEta);
    }
  }, [order?.dropoffLat, order?.dropoffLng]);

  const refreshRoute = useCallback(async (driverLat, driverLng) => {
    const destLat = order?.dropoffLat;
    const destLng = order?.dropoffLng;
    if (!destLat || !destLng || !driverLat || !driverLng) return;
    const result = await fetchOrsRoute(driverLat, driverLng, destLat, destLng);
    if (result) {
      setRoutePolyline(result?.polyline);
      const traffic = getTrafficMultiplier();
      const adjustedMin = Math.round(result?.durationMin * traffic?.multiplier);
      setCurrentETA(adjustedMin);
      setTrafficETA(adjustedMin);
    }
  }, [order?.dropoffLat, order?.dropoffLng]);

  const triggerArrivalSequence = useCallback(async () => {
    if (geofenceTriggeredRef?.current) return;
    geofenceTriggeredRef.current = true;
    fireBrowserNotification('Your driver has arrived! 🎉', 'Your OnTheGo driver is at your destination.');
    setShowArrivalModal(true);
    try {
      if (order?.id && order?.driverId) {
        const { data, error } = await supabase?.rpc('complete_order_on_arrival', {
          p_order_id: order?.id,
          p_driver_profile_id: order?.driverId,
        });
        if (!error && data?.success) {
          setLiveStatus('delivered');
          setOrderCompleted(true);
          clearInterval(simulationRef?.current);
        }
      }
    } catch (err) {
      console.error('Auto-complete order error:', err);
    }
    setTimeout(() => {
      setShowArrivalModal(false);
      navigate(`/rating-submission-screen?orderId=${order?.id}&reviewerRole=customer`);
    }, 3000);
  }, [order?.id, order?.driverId, navigate]);

  useEffect(() => {
    if (!order?.id) return;
    setConnectionStatus('connecting');

    orderChannelRef.current = realtimeService?.subscribeToOrderUpdates(order?.id, (payload) => {
      if (payload?.new) {
        const newStatus = payload?.new?.status;
        setLiveStatus(newStatus);
        setOrder(prev => ({
          ...prev,
          status: newStatus,
          driverId: payload?.new?.driver_id ?? prev?.driverId,
        }));
        if (newStatus === 'delivered' || newStatus === 'cancelled') {
          clearInterval(simulationRef?.current);
          if (newStatus === 'cancelled') {
            setOrderCancelled(true);
            setTimeout(() => navigate('/customer-booking-screen'), 2000);
          }
        }
      }
      setConnectionStatus('live');
    });

    if (order?.driverId) {
      locationChannelRef.current = realtimeService?.subscribeToDriverLocation(order?.driverId, (payload) => {
        if (payload?.new) {
          const { latitude, longitude, heading, speed } = payload?.new;
          let computedHeading = heading;
          if (prevPositionRef?.current && (heading == null || heading === 0)) {
            computedHeading = calculateBearing(
              prevPositionRef?.current?.lat,
              prevPositionRef?.current?.lng,
              latitude,
              longitude
            );
          }
          const newPos = { lat: latitude, lng: longitude, heading: computedHeading, speed };
          prevPositionRef.current = { lat: latitude, lng: longitude };
          setDriverPosition(newPos);
          setIsRealGpsStreaming(true);
          if (gpsTimeoutRef?.current) clearTimeout(gpsTimeoutRef?.current);
          gpsTimeoutRef.current = setTimeout(() => setIsRealGpsStreaming(false), 15000);
          recalculateETA(latitude, longitude, speed);
          const destLat = order?.dropoffLat;
          const destLng = order?.dropoffLng;
          if (destLat && destLng && !geofenceTriggeredRef?.current) {
            if (isWithinGeofence(latitude, longitude, destLat, destLng)) {
              triggerArrivalSequence();
            }
          }
          const pickupLat = order?.pickupLat;
          const pickupLng = order?.pickupLng;
          const dropoffLat = order?.dropoffLat;
          const dropoffLng = order?.dropoffLng;
          if (pickupLat && pickupLng && dropoffLat && dropoffLng) {
            const totalDist = haversineKm(pickupLat, pickupLng, dropoffLat, dropoffLng);
            const remainingDist = haversineKm(latitude, longitude, dropoffLat, dropoffLng);
            const progress = Math.min(100, Math.max(0, ((totalDist - remainingDist) / totalDist) * 100));
            setRouteProgress(progress);
          }
        }
        setConnectionStatus('live');
      });

      driverService?.getLatestDriverLocation(order?.driverId)?.then(loc => {
        if (loc) {
          const newPos = { lat: loc?.latitude, lng: loc?.longitude, heading: loc?.heading, speed: loc?.speed };
          prevPositionRef.current = { lat: loc?.latitude, lng: loc?.longitude };
          setDriverPosition(newPos);
          recalculateETA(loc?.latitude, loc?.longitude, loc?.speed);
          refreshRoute(loc?.latitude, loc?.longitude);
        }
      })?.catch(() => {});
    }

    setConnectionStatus('live');

    simulationRef.current = setInterval(() => {
      setRouteProgress(prev => {
        if (prev >= 100) { clearInterval(simulationRef?.current); return 100; }
        return prev + 0.3;
      });
    }, 1000);

    routeRefreshRef.current = setInterval(() => {
      if (prevPositionRef?.current) {
        refreshRoute(prevPositionRef?.current?.lat, prevPositionRef?.current?.lng);
      }
    }, 30000);

    return () => {
      realtimeService?.unsubscribe(orderChannelRef?.current);
      realtimeService?.unsubscribe(locationChannelRef?.current);
      clearInterval(simulationRef?.current);
      clearInterval(routeRefreshRef?.current);
      if (gpsTimeoutRef?.current) clearTimeout(gpsTimeoutRef?.current);
      setConnectionStatus('offline');
    };
  }, [order?.id, order?.driverId, recalculateETA, refreshRoute, triggerArrivalSequence]);

  const handleETAUpdate = (minutes) => setCurrentETA(minutes);
  const handleCall = () => setShowCallModal(true);
  const handleChat = () => setShowChatModal(true);

  const handleCancelOrder = async (reason) => {
    try {
      if (order?.id) await orderService?.cancelOrder(order?.id, reason);
      setOrderCancelled(true);
      setTimeout(() => navigate('/customer-booking-screen'), 2000);
    } catch (err) {
      console.error('Cancel error:', err);
      setOrderCancelled(true);
      setTimeout(() => navigate('/customer-booking-screen'), 2000);
    }
  };

  const displayStops = stops?.length > 0 ? stops?.map(s => ({
    id: s?.id,
    address: s?.address,
    recipient: s?.recipient,
    note: s?.note,
    status: s?.status,
    completedAt: s?.completedAt ? new Date(s.completedAt)?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : null,
    eta: s?.eta,
  })) : [
    { id: 1, address: order?.pickupAddress || '14 Kimathi Street, CBD', recipient: 'Pickup', note: null, status: 'completed', completedAt: '2:35 PM', eta: null },
    { id: 2, address: order?.dropoffAddress || 'Destination', recipient: 'Drop-off', note: null, status: 'active', completedAt: null, eta: currentETA },
  ];

  const effectiveStatus = liveStatus || order?.status;
  const currentStatus = effectiveStatus === 'driver_assigned' ? 'driver_assigned'
    : effectiveStatus === 'picked_up' ? 'picked_up'
    : effectiveStatus === 'in_transit' ? 'in_transit'
    : effectiveStatus === 'en_route' ? 'in_transit'
    : effectiveStatus === 'delivered' ? 'delivered'
    : effectiveStatus === 'request_sent' ? 'request_sent'
    : effectiveStatus === 'confirmed'? 'driver_assigned' :'request_sent';

  const isDeliveryOrder = order?.serviceType === 'delivery';

  const connectionBadge = {
    connecting: { color: 'text-yellow-400', bg: 'bg-yellow-500/20 border-yellow-500/30', dot: 'bg-yellow-400', label: 'Connecting' },
    live: { color: 'text-green-400', bg: 'bg-green-500/20 border-green-500/30', dot: 'bg-green-400 animate-pulse', label: 'Live' },
    offline: { color: 'text-red-400', bg: 'bg-red-500/20 border-red-500/30', dot: 'bg-red-400', label: 'Offline' },
  }?.[connectionStatus] || { color: 'text-green-400', bg: 'bg-green-500/20 border-green-500/30', dot: 'bg-green-400 animate-pulse', label: 'Live' };

  if (orderCancelled) {
    return (
      <div className="min-h-screen bg-[#0a1628] flex items-center justify-center px-4">
        <div className="text-center animate-fade-in-up">
          <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
            <svg viewBox="0 0 24 24" fill="#ef4444" className="w-10 h-10">
              <path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z" />
            </svg>
          </div>
          <h2 className="heading-1 text-white mb-2">Order Cancelled</h2>
          <p className="body-text text-white/50">Redirecting you to booking...</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#060e1e] flex flex-col">
        {/* Skeleton header */}
        <div className="bg-[#0a1628]/95 border-b border-white/10 px-4 py-4">
          <div className="max-w-lg mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-white/10 animate-pulse" />
              <div className="space-y-1.5">
                <div className="h-3 w-20 rounded-full bg-white/10 animate-pulse" />
                <div className="h-4 w-32 rounded-full bg-white/15 animate-pulse" />
              </div>
            </div>
            <div className="h-7 w-20 rounded-full bg-[#ffd700]/20 animate-pulse" />
          </div>
        </div>
        {/* Skeleton map */}
        <div className="flex-1 bg-[#0a1628]/40 relative">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center space-y-3">
              <div className="w-16 h-16 rounded-full bg-white/5 animate-pulse mx-auto" />
              <div className="h-3 w-32 rounded-full bg-white/10 animate-pulse mx-auto" />
            </div>
          </div>
          {/* Skeleton grid overlay */}
          <svg className="absolute inset-0 w-full h-full opacity-5" viewBox="0 0 400 400" preserveAspectRatio="xMidYMid slice">
            {[0,50,100,150,200,250,300,350,400]?.map(y => (
              <line key={`h${y}`} x1="0" y1={y} x2="400" y2={y} stroke="#ffd700" strokeWidth="0.5" />
            ))}
            {[0,50,100,150,200,250,300,350,400]?.map(x => (
              <line key={`v${x}`} x1={x} y1="0" x2={x} y2="400" stroke="#ffd700" strokeWidth="0.5" />
            ))}
          </svg>
        </div>
        {/* Skeleton bottom sheet */}
        <div className="bg-[#0a1628] rounded-t-3xl px-4 pt-5 pb-8 space-y-4">
          <div className="w-10 h-1 rounded-full bg-white/20 mx-auto mb-4" />
          {/* ETA skeleton */}
          <div className="flex items-center gap-4 p-4 bg-white/5 rounded-2xl">
            <div className="w-14 h-14 rounded-full bg-white/10 animate-pulse flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <div className="h-3 w-24 rounded-full bg-white/10 animate-pulse" />
              <div className="h-8 w-20 rounded-lg bg-white/15 animate-pulse" />
            </div>
          </div>
          {/* Driver card skeleton */}
          <div className="flex items-center gap-3 p-4 bg-white/5 rounded-2xl">
            <div className="w-12 h-12 rounded-full bg-white/10 animate-pulse flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-28 rounded-full bg-white/15 animate-pulse" />
              <div className="h-3 w-20 rounded-full bg-white/10 animate-pulse" />
            </div>
            <div className="w-10 h-10 rounded-xl bg-white/10 animate-pulse" />
          </div>
          {/* Status timeline skeleton */}
          <div className="space-y-3">
            {[0,1,2]?.map(i => (
              <SkeletonRow key={i} dark cols={2} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a1628] relative overflow-hidden" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Spinner overlay for order cancellation */}
      <SpinnerOverlay
        visible={cancellingOrder}
        title="Cancelling order..."
        message="Notifying driver and processing cancellation"
      />
      {/* Arrival success toast */}
      <SuccessToast
        visible={arrivalToast}
        message="Driver has arrived! 🎉"
        subMessage="Your driver is at the destination"
        onDismiss={() => setArrivalToast(false)}
        duration={4000}
        position="top"
      />
      {/* ─── FULL-SCREEN MAP MODE ──────────────────────────────────────────── */}
      {fullScreenMap && (
        <div className="fixed inset-0 z-50 bg-[#0a1628]">
          {/* Traffic strip */}
          <div
            className="absolute top-0 left-0 right-0 z-10 flex items-center justify-center gap-2 py-2 px-4"
            style={{ background: `${trafficInfo?.color}22`, borderBottom: `2px solid ${trafficInfo?.color}50` }}
          >
            <div className="w-2 h-2 rounded-full" style={{ background: trafficInfo?.color }} />
            <span className="text-xs font-bold" style={{ color: trafficInfo?.color }}>
              Traffic: {trafficInfo?.label}
            </span>
            {trafficETA && (
              <span className="text-xs text-white/60 ml-2">· ETA {trafficETA} min</span>
            )}
          </div>
          {/* Full map */}
          <div className="absolute inset-0 pt-8">
            <TrackingMap
              stops={displayStops}
              driverPosition={driverPosition}
              routeProgress={routeProgress}
              isRealGpsStreaming={isRealGpsStreaming}
              routePolyline={routePolyline}
              trafficInfo={trafficInfo}
              geofenceReached={showArrivalModal || orderCompleted}
            />
          </div>
          {/* ETA overlay on full-screen map */}
          <div className="absolute bottom-24 left-4 right-4 z-10">
            <div className="bg-[#0a1628]/90 backdrop-blur-md rounded-2xl p-4 border border-[#ffd700]/20 flex items-center justify-between shadow-xl">
              <div>
                <p className="text-white/40 text-xs font-medium uppercase tracking-wider">ETA</p>
                <p className="text-[#ffd700] font-black text-3xl leading-none mt-0.5">
                  {currentETA} <span className="text-sm font-normal text-white/60">min</span>
                </p>
              </div>
              <div className="h-10 w-px bg-white/10" />
              <div className="text-right">
                <p className="text-white/40 text-xs font-medium uppercase tracking-wider">Driver</p>
                <p className="text-white font-bold text-sm mt-0.5">{mockDriver?.name}</p>
                <p className="text-[#ffd700]/70 text-xs font-semibold">{mockDriver?.licensePlate}</p>
              </div>
              <div className="h-10 w-px bg-white/10" />
              <div className="text-right">
                <p className="text-white/40 text-xs font-medium uppercase tracking-wider">Status</p>
                <div className="flex items-center gap-1.5 mt-0.5 justify-end">
                  <div className={`w-2 h-2 rounded-full ${connectionBadge?.dot}`} />
                  <span className={`text-xs font-bold ${connectionBadge?.color}`}>{connectionBadge?.label}</span>
                </div>
              </div>
            </div>
          </div>
          {/* Exit full-screen */}
          <button
            onClick={() => setFullScreenMap(false)}
            className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 bg-[#ffd700] text-[#0a1628] font-bold text-sm px-6 py-3.5 rounded-2xl shadow-xl active:scale-95 transition-all"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4">
              <path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3" />
            </svg>
            Exit Full Screen
          </button>
        </div>
      )}
      {/* ─── HEADER ────────────────────────────────────────────────────────── */}
      <div className="fixed top-0 left-0 right-0 z-30 bg-[#0a1628]/95 backdrop-blur-md border-b border-[#ffd700]/10">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center hover:bg-white/15 transition-colors flex-shrink-0 active:scale-95"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" className="w-4 h-4">
              <path d="M19 12H5M12 5l-7 7 7 7" />
            </svg>
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-md bg-[#ffd700] flex items-center justify-center flex-shrink-0">
<span className="text-[#0a1628] font-black text-xs">OG</span>
              </div>
              <span className="text-white font-extrabold text-sm tracking-tight">Order Tracking</span>
            </div>
            <p className="text-white/40 text-xs truncate mt-0.5">Order #{order?.orderNumber || 'Loading...'}</p>
          </div>
          {/* Traffic indicator */}
          <div className="flex items-center gap-1.5 rounded-full px-2.5 py-1 border mr-1" style={{ background: `${trafficInfo?.color}18`, borderColor: `${trafficInfo?.color}40` }}>
            <div className="w-2 h-2 rounded-full" style={{ background: trafficInfo?.color }} />
            <span className="text-xs font-bold" style={{ color: trafficInfo?.color }}>{trafficInfo?.label}</span>
          </div>
          <div className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 border ${connectionBadge?.bg}`}>
            <div className={`w-1.5 h-1.5 rounded-full ${connectionBadge?.dot}`}></div>
            <span className={`text-xs font-bold ${connectionBadge?.color}`}>{connectionBadge?.label}</span>
          </div>
        </div>
      </div>
      {/* ─── FULL-SCREEN MAP (60% of viewport) ────────────────────────────── */}
      <div
        className="fixed left-0 right-0 z-10 transition-all duration-500"
        style={{ top: '60px', height: sheetExpanded ? '35vh' : '60vh' }}
      >
        <div className="max-w-lg mx-auto h-full relative">
          <TrackingMap
            stops={displayStops}
            driverPosition={driverPosition}
            routeProgress={routeProgress}
            isRealGpsStreaming={isRealGpsStreaming}
            routePolyline={routePolyline}
            trafficInfo={trafficInfo}
            geofenceReached={showArrivalModal || orderCompleted}
          />

          {/* ─── LIVE ETA OVERLAY ON MAP ──────────────────────────────────── */}
          {etaOverlayVisible && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 animate-fade-in-up">
              <div
                className="flex items-center gap-3 bg-[#0a1628]/90 backdrop-blur-md rounded-2xl px-4 py-2.5 border border-[#ffd700]/30 shadow-xl"
                style={{ minWidth: '200px' }}
              >
                <div className="text-center">
                  <p className="text-white/40 text-[10px] font-bold uppercase tracking-widest">ETA</p>
                  <p className="text-[#ffd700] font-black text-xl leading-none">{currentETA}<span className="text-xs text-white/50 font-normal ml-0.5">m</span></p>
                </div>
                <div className="w-px h-8 bg-white/10" />
                <div className="flex-1">
                  <p className="text-white font-bold text-xs truncate">{mockDriver?.name}</p>
                  <p className="text-white/40 text-[10px]">{mockDriver?.vehicleType} · {mockDriver?.licensePlate}</p>
                </div>
                {isRealGpsStreaming && (
                  <div className="relative flex items-center justify-center">
                    <div className="absolute w-4 h-4 rounded-full bg-green-400/30 animate-ping" />
                    <div className="w-2 h-2 rounded-full bg-green-400" />
                  </div>
                )}
                <button
                  onClick={() => setEtaOverlayVisible(false)}
                  className="text-white/30 hover:text-white/60 transition-colors ml-1"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Full-screen toggle */}
          <button
            onClick={() => setFullScreenMap(true)}
            className="absolute bottom-4 right-4 z-20 flex items-center gap-1.5 bg-[#0a1628]/85 backdrop-blur-sm text-white text-xs font-bold px-3 py-2 rounded-xl border border-[#ffd700]/30 hover:bg-[#0a1628] transition-all active:scale-95 shadow-lg"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5 text-[#ffd700]">
              <path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3" />
            </svg>
            Full Screen
          </button>

          {/* Restore ETA overlay button */}
          {!etaOverlayVisible && (
            <button
              onClick={() => setEtaOverlayVisible(true)}
              className="absolute top-4 left-1/2 -translate-x-1/2 z-20 bg-[#0a1628]/80 backdrop-blur-sm text-[#ffd700] text-xs font-bold px-3 py-1.5 rounded-full border border-[#ffd700]/30 active:scale-95 transition-all"
            >
              Show ETA
            </button>
          )}
        </div>
      </div>
      {/* ─── FLOATING BOTTOM CARD (Uber-style) ────────────────────────────── */}
      <div
        className="fixed left-0 right-0 bottom-0 z-20 transition-all duration-500"
        style={{ top: sheetExpanded ? 'calc(35vh + 60px)' : 'calc(60vh + 60px)' }}
      >
        <div className="max-w-lg mx-auto h-full flex flex-col">

          {/* ─── DRAG HANDLE + STATUS BAR ─────────────────────────────────── */}
          <div
            className="bg-white rounded-t-3xl pt-3 pb-3 px-4 flex flex-col items-center cursor-pointer border-t border-[#0a1628]/8 shadow-[0_-8px_32px_rgba(10,22,40,0.15)] flex-shrink-0"
            onClick={() => setSheetExpanded(!sheetExpanded)}
          >
            <div className="w-10 h-1 rounded-full bg-[#0a1628]/15 mb-3"></div>

            {/* Driver quick info row */}
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-3">
                {/* Driver avatar mini */}
                <div className="relative">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#0a1628] to-[#1a365d] flex items-center justify-center text-lg shadow-md">
                    {mockDriver?.avatar}
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-green-400 border-2 border-white"></div>
                </div>
                <div>
                  <p className="font-extrabold text-[#0a1628] text-sm leading-tight">{mockDriver?.name}</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#ffd700] animate-pulse"></div>
                    <span className="text-xs text-[#0a1628]/50 font-medium">
                      {effectiveStatus?.replace(/_/g, ' ') || 'En Route'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* ETA pill */}
                <div className="bg-[#0a1628] rounded-xl px-3 py-1.5 flex items-center gap-1.5">
                  <svg viewBox="0 0 24 24" fill="#ffd700" className="w-3 h-3">
                    <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z" />
                  </svg>
                  <span className="text-[#ffd700] font-black text-sm">{currentETA} min</span>
                </div>
                <svg
                  viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                  className={`w-4 h-4 text-[#0a1628]/40 transition-transform duration-300 ${sheetExpanded ? 'rotate-180' : ''}`}
                >
                  <path d="M18 15l-6-6-6 6" />
                </svg>
              </div>
            </div>

            {/* Quick action buttons - always visible */}
            <div className="flex gap-2 w-full mt-3">
              <button
                onClick={(e) => { e?.stopPropagation(); handleCall(); }}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-[#0a1628] text-[#ffd700] font-bold text-sm hover:bg-[#1a365d] transition-all active:scale-[0.97] shadow-[0_4px_12px_rgba(10,22,40,0.2)]"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                  <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
                </svg>
                Call Driver
              </button>
              <button
                onClick={(e) => { e?.stopPropagation(); handleChat(); }}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-[#ffd700]/15 text-[#0a1628] font-bold text-sm hover:bg-[#ffd700]/25 transition-all active:scale-[0.97] border-2 border-[#ffd700]/30"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                  <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z" />
                </svg>
                Chat
              </button>
              <button
                onClick={(e) => { e?.stopPropagation(); setFullScreenMap(true); }}
                className="w-12 h-12 rounded-xl bg-[#0a1628]/8 flex items-center justify-center hover:bg-[#0a1628]/12 transition-all active:scale-[0.97] border border-[#0a1628]/10"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="#0a1628" strokeWidth="2" className="w-4 h-4 opacity-60">
                  <path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3" />
                </svg>
              </button>
            </div>
          </div>

          {/* ─── SCROLLABLE CONTENT ───────────────────────────────────────── */}
          <div className="bg-white flex-1 overflow-y-auto">
            <div className="px-4 pb-8 space-y-4 pt-3">

              {/* ETA Countdown - full detail */}
              <ETACountdown initialMinutes={18} liveMinutes={currentETA} onUpdate={handleETAUpdate} />

              {/* Order Status Timeline — delivery-aware */}
              <OrderStatusTimeline currentStatus={currentStatus} serviceType={order?.serviceType || 'ride'} />

              {/* Delivery details panel */}
              {isDeliveryOrder && order?.deliveryDetails && (
                <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-lg">📦</span>
                    <h3 className="text-sm font-bold text-blue-800">Delivery Details</h3>
                  </div>
                  <div className="space-y-2">
                    {order?.deliveryDetails?.package_type && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-blue-600/70">Package Type</span>
                        <span className="text-xs font-bold text-blue-800 capitalize">{order?.deliveryDetails?.package_type}</span>
                      </div>
                    )}
                    {order?.deliveryDetails?.weight && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-blue-600/70">Weight</span>
                        <span className="text-xs font-bold text-blue-800">{order?.deliveryDetails?.weight} kg</span>
                      </div>
                    )}
                    {order?.deliveryDetails?.sender_name && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-blue-600/70">Sender</span>
                        <span className="text-xs font-bold text-blue-800">{order?.deliveryDetails?.sender_name}</span>
                      </div>
                    )}
                    {order?.deliveryDetails?.recipient_name && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-blue-600/70">Recipient</span>
                        <span className="text-xs font-bold text-blue-800">{order?.deliveryDetails?.recipient_name}</span>
                      </div>
                    )}
                    {order?.deliveryDetails?.recipient_phone && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-blue-600/70">Recipient Phone</span>
                        <a href={`tel:${order?.deliveryDetails?.recipient_phone}`} className="text-xs font-bold text-blue-600 underline">{order?.deliveryDetails?.recipient_phone}</a>
                      </div>
                    )}
                    {order?.deliveryDetails?.notes && (
                      <div className="pt-1 border-t border-blue-200">
                        <span className="text-xs text-blue-600/70 block mb-0.5">Notes</span>
                        <span className="text-xs text-blue-800">{order?.deliveryDetails?.notes}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Proof of Delivery — shown when order is delivered and proof exists */}
              {isDeliveryOrder && order?.deliveryDetails?.proof_of_delivery_url && (
                <div className="bg-green-50 border border-green-200 rounded-2xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-lg">✅</span>
                    <h3 className="text-sm font-bold text-green-800">Proof of Delivery</h3>
                    {order?.deliveryDetails?.proof_uploaded_at && (
                      <span className="ml-auto text-xs text-green-600/70">
                        {new Date(order?.deliveryDetails?.proof_uploaded_at)?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                  <div className="rounded-xl overflow-hidden border border-green-200">
                    <img
                      src={order?.deliveryDetails?.proof_of_delivery_url}
                      alt="Proof of delivery photo showing package at recipient location"
                      className="w-full h-48 object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                  <p className="text-xs text-green-700/70 mt-2 text-center">
                    Photo taken by driver at delivery location
                  </p>
                </div>
              )}

              {/* Driver Card - full detail */}
              <DriverCard driver={mockDriver} onCall={handleCall} onChat={handleChat} />

              {/* Delivery Stops */}
              <DeliveryStopList stops={displayStops} />

              {/* Order Actions */}
              <OrderActions onShareTracking={() => {}} onCancelOrder={handleCancelOrder} />

              {/* Order Summary */}
              <div className="card-base p-4">
                <h3 className="text-xs font-extrabold text-[#0a1628]/40 uppercase tracking-widest mb-3">Order Summary</h3>
                <div className="space-y-2.5">
                  {[
                    { label: 'Service', value: order?.serviceType || 'Delivery' },
                    { label: 'Total Stops', value: `${displayStops?.length} locations` },
                    { label: 'Distance', value: order?.distanceKm ? `${order?.distanceKm} km` : '8.4 km total' },
                    { label: 'Payment', value: order?.totalAmount ? `M-Pesa · KSh ${order?.totalAmount}` : 'M-Pesa · KSh 420' },
                  ]?.map(item => (
                    <div key={item?.label} className="flex items-center justify-between">
                      <span className="body-text">{item?.label}</span>
                      <span className="text-sm font-bold text-[#0a1628]">{item?.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ─── ARRIVAL MODAL ─────────────────────────────────────────────────── */}
      {showArrivalModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="w-full max-w-sm bg-[#0a1628] rounded-3xl p-8 text-center shadow-2xl border border-[#ffd700]/30 relative overflow-hidden animate-fade-in-up">
            <div className="absolute inset-0 bg-gradient-to-br from-[#ffd700]/5 to-transparent pointer-events-none" />
            <div className="relative mx-auto mb-6 w-24 h-24">
              <div className="absolute inset-0 rounded-full bg-[#ffd700]/20 animate-ping" />
              <div className="absolute inset-2 rounded-full bg-[#ffd700]/30 animate-pulse" />
              <div className="relative w-24 h-24 rounded-full bg-[#ffd700] flex items-center justify-center shadow-xl">
                <svg viewBox="0 0 24 24" fill="#0a1628" className="w-12 h-12">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-4.47 7-10c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                </svg>
              </div>
            </div>
            <h2 className="text-white font-black text-2xl mb-2">Your driver has arrived!</h2>
            <p className="text-white/60 text-sm mb-2">{mockDriver?.name} is waiting at your destination.</p>
            <p className="text-[#ffd700] text-xs font-bold mb-8">{mockDriver?.vehicleType} · {mockDriver?.licensePlate}</p>
            <button
              onClick={() => {
                setShowArrivalModal(false);
                navigate(`/rating-submission-screen?orderId=${order?.id}&reviewerRole=customer`);
              }}
              className="btn-primary-full text-base py-4"
            >
              Got it, I'm on my way!
            </button>
          </div>
        </div>
      )}
      {/* ─── RATING MODAL ──────────────────────────────────────────────────── */}
      <RatingModal
        isOpen={showRatingModal}
        onClose={() => setShowRatingModal(false)}
        orderId={order?.id}
        reviewerRole="customer"
        driverName={mockDriver?.name}
      />
      {/* ─── CALL MODAL ────────────────────────────────────────────────────── */}
      {showCallModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-xs bg-[#0a1628] rounded-3xl p-6 text-center shadow-2xl border border-[#ffd700]/20 animate-fade-in-up">
            <div className="w-20 h-20 rounded-full bg-[#ffd700]/10 flex items-center justify-center text-4xl">{mockDriver?.avatar}</div>
            <h3 className="text-white font-extrabold text-lg mb-1">{mockDriver?.name}</h3>
            <p className="text-white/40 text-sm mb-1">{mockDriver?.vehicleType}</p>
            <p className="text-[#ffd700] font-bold text-base mb-6">{mockDriver?.phone}</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowCallModal(false)}
                className="flex-1 py-3.5 rounded-xl bg-white/10 text-white font-bold text-sm hover:bg-white/15 transition-all active:scale-[0.97]"
              >
                Cancel
              </button>
              <a
                href={`tel:${mockDriver?.phone}`}
                className="flex-1 py-3.5 rounded-xl bg-[#ffd700] text-[#0a1628] font-black text-sm flex items-center justify-center hover:bg-[#f0c800] transition-all active:scale-[0.97] shadow-[0_4px_14px_rgba(255,215,0,0.35)]"
              >
                Call Now
              </a>
            </div>
          </div>
        </div>
      )}
      {/* ─── CHAT MODAL ────────────────────────────────────────────────────── */}
      {showChatModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-lg bg-white rounded-t-3xl p-6 shadow-2xl animate-slide-up-in">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#0a1628]/10 flex items-center justify-center text-xl">{mockDriver?.avatar}</div>
                <div>
                  <p className="font-extrabold text-[#0a1628] text-sm">{mockDriver?.name}</p>
                  <p className="text-[#0a1628]/40 text-xs">Driver</p>
                </div>
              </div>
              <button
                onClick={() => setShowChatModal(false)}
                className="w-8 h-8 rounded-full bg-[#0a1628]/8 flex items-center justify-center hover:bg-[#0a1628]/12 transition-colors active:scale-95"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 text-[#0a1628]">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="bg-[#0a1628]/4 rounded-2xl p-4 mb-4 min-h-32 flex items-center justify-center">
              <p className="text-[#0a1628]/40 text-sm text-center">Chat feature coming soon</p>
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Type a message..."
                className="flex-1 px-4 py-3 bg-[#0a1628]/4 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#ffd700]/50"
              />
              <button className="w-12 h-12 rounded-xl bg-[#0a1628] flex items-center justify-center hover:bg-[#1a365d] transition-colors active:scale-95 shadow-[0_4px_12px_rgba(10,22,40,0.2)]">
                <svg viewBox="0 0 24 24" fill="#ffd700" className="w-5 h-5">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderTrackingScreen;
